/**
 * iter255 (C1) — Interior / Owner Asset entry modal.
 * Feeds the SAME `onboarding_line_item` pool with:
 *   source_type      = FRANCHISE_OWNER_INTERIOR
 *   destination_type = ASSET
 * so it flows into the Asset Opening register automatically.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Upload } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CONDITIONS = ['New', 'Good', 'Fair', 'Repair Required', 'Damaged', 'Missing', 'Scrap'];

export function InteriorAssetModal({ open, onClose, onboardingId, token, onSaved }) {
  const [cats, setCats] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({
    category_code: '', subcategory: '', item_name: '',
    quantity: 1, unit: 'nos', condition: 'New',
    rate: '', vendor: '', invoice_number: '', purchase_date: '',
    serial_number: '', warranty_expiry: '',
    location: '', responsible_person: '', remarks: '',
    bill_url: '',
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const c = await fetch(`${API}/api/onboarding-interior/categories/list`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      }).then(r => r.json()).catch(() => ({ categories: [] }));
      setCats(c.categories || []);
      await refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onboardingId]);

  const refresh = async () => {
    if (!onboardingId) return;
    const r = await fetch(`${API}/api/onboarding-interior/interior-asset/list/${onboardingId}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    }).then(r => r.json()).catch(() => ({ rows: [] }));
    setRows(r.rows || []);
  };

  const uploadBill = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('token', token);
      fd.append('purpose', 'interior_asset_bill');
      const res = await fetch(`${API}/api/documents/upload`, { method: 'POST', body: fd });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Upload failed');
      setForm(f => ({ ...f, bill_url: j.file_url || j.url || '' }));
      toast.success('Bill / proof uploaded');
    } catch (e) { toast.error(e.message); }
    finally { setUploading(false); }
  };

  const currentCat = cats.find(c => c.code === form.category_code);

  const submit = async () => {
    if (!form.category_code) return toast.error('Pick a category');
    if (!form.item_name.trim()) return toast.error('Item name required');
    if (!form.quantity || Number(form.quantity) <= 0) return toast.error('Quantity must be > 0');
    setBusy(true);
    try {
      const body = {
        token, onboarding_id: onboardingId,
        ...form,
        quantity: Number(form.quantity),
        rate: form.rate === '' ? null : Number(form.rate),
      };
      const res = await fetch(`${API}/api/onboarding-interior/interior-asset/add`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Save failed');
      toast.success(`Added — ${form.item_name}${j.value_note ? ` (${j.value_note})` : ''}`);
      setForm(f => ({ ...f, item_name: '', quantity: 1, rate: '', serial_number: '', warranty_expiry: '', remarks: '', bill_url: '' }));
      await refresh();
      onSaved && onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const delRow = async (line_id) => {
    if (!window.confirm('Remove this interior asset row?')) return;
    try {
      const res = await fetch(`${API}/api/onboarding-interior/interior-asset/delete`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, line_id }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Delete failed');
      await refresh();
      onSaved && onSaved();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto" data-testid="interior-asset-modal">
        <DialogHeader><DialogTitle>Add Interior / Owner Asset</DialogTitle></DialogHeader>
        <div className="text-[11px] text-muted-foreground bg-blue-50 border border-blue-200 rounded p-2">
          Franchise Owner supplied items (sofa, tables, chairs, AC, curtains, signage, decor, CCTV, ...). These feed the SAME Opening Asset Register. Rate is optional — leave blank for "VALUE NOT AVAILABLE".
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-1">
            <Label>Category *</Label>
            <Select value={form.category_code} onValueChange={v => setForm(f => ({ ...f, category_code: v, subcategory: '' }))}>
              <SelectTrigger data-testid="ia-category"><SelectValue placeholder="Pick category" /></SelectTrigger>
              <SelectContent>
                {cats.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Subcategory</Label>
            <Select value={form.subcategory} onValueChange={v => setForm(f => ({ ...f, subcategory: v }))} disabled={!currentCat}>
              <SelectTrigger data-testid="ia-subcategory"><SelectValue placeholder={currentCat ? 'Pick subcategory' : 'Category first'} /></SelectTrigger>
              <SelectContent>
                {(currentCat?.subcategories || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Item Name *</Label>
            <Input value={form.item_name} onChange={e => setForm(f => ({ ...f, item_name: e.target.value }))} placeholder="e.g. Sofa, Dining Table, AC" data-testid="ia-item-name" />
          </div>
          <div>
            <Label>Quantity *</Label>
            <Input type="number" min="0" step="any" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} data-testid="ia-qty" />
          </div>
          <div>
            <Label>Unit</Label>
            <Input value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} data-testid="ia-unit" />
          </div>
          <div>
            <Label>Condition</Label>
            <Select value={form.condition} onValueChange={v => setForm(f => ({ ...f, condition: v }))}>
              <SelectTrigger data-testid="ia-condition"><SelectValue /></SelectTrigger>
              <SelectContent>{CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Rate <span className="text-[10px] text-muted-foreground">(optional — blank = VALUE NOT AVAILABLE)</span></Label>
            <Input type="number" min="0" step="any" value={form.rate} onChange={e => setForm(f => ({ ...f, rate: e.target.value }))} placeholder="—" data-testid="ia-rate" />
          </div>
          <div>
            <Label>Vendor / Contractor</Label>
            <Input value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))} data-testid="ia-vendor" />
          </div>
          <div>
            <Label>Purchase Date</Label>
            <Input type="date" value={form.purchase_date} onChange={e => setForm(f => ({ ...f, purchase_date: e.target.value }))} data-testid="ia-purchase-date" />
          </div>
          <div>
            <Label>Invoice / Bill Reference</Label>
            <Input value={form.invoice_number} onChange={e => setForm(f => ({ ...f, invoice_number: e.target.value }))} data-testid="ia-invoice-number" />
          </div>
          <div>
            <Label>Serial Number</Label>
            <Input value={form.serial_number} onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))} data-testid="ia-serial" />
          </div>
          <div>
            <Label>Warranty Expiry</Label>
            <Input type="date" value={form.warranty_expiry} onChange={e => setForm(f => ({ ...f, warranty_expiry: e.target.value }))} data-testid="ia-warranty" />
          </div>
          <div>
            <Label>Location in Centre</Label>
            <Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} data-testid="ia-location" />
          </div>
          <div>
            <Label>Responsible Person</Label>
            <Input value={form.responsible_person} onChange={e => setForm(f => ({ ...f, responsible_person: e.target.value }))} data-testid="ia-responsible" />
          </div>
          <div>
            <Label>Upload Bill / Proof / Photo</Label>
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center gap-1 px-3 py-1 border rounded cursor-pointer text-xs hover:bg-muted">
                <Upload className="w-3 h-3" /> {uploading ? 'Uploading…' : (form.bill_url ? 'Replace' : 'Upload')}
                <input type="file" hidden accept="image/*,application/pdf" onChange={e => uploadBill(e.target.files?.[0])} data-testid="ia-bill-upload" />
              </label>
              {form.bill_url && <a href={form.bill_url} target="_blank" rel="noreferrer" className="text-[11px] text-primary underline">View</a>}
            </div>
          </div>
          <div className="md:col-span-3">
            <Label>Remarks</Label>
            <Textarea rows={2} value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} data-testid="ia-remarks" />
          </div>
        </div>
        <Button onClick={submit} disabled={busy} className="w-fit" data-testid="ia-submit"><Plus className="w-4 h-4 mr-1" />{busy ? 'Adding…' : 'Add to Opening Asset Register'}</Button>

        {/* Existing rows for this onboarding */}
        <div className="pt-3">
          <div className="text-xs font-semibold mb-2">Owner Interior Assets — {rows.length} row(s)</div>
          <div className="border rounded overflow-x-auto max-h-64">
            <table className="w-full text-xs" data-testid="ia-list-table">
              <thead className="bg-muted/40 sticky top-0">
                <tr className="text-left border-b">
                  <th className="p-2">Item</th>
                  <th className="p-2">Category / Subcategory</th>
                  <th className="p-2 text-right">Qty</th>
                  <th className="p-2">Unit</th>
                  <th className="p-2">Condition</th>
                  <th className="p-2 text-right">Rate</th>
                  <th className="p-2">Vendor</th>
                  <th className="p-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-b" data-testid={`ia-row-${r.id}`}>
                    <td className="p-2 font-medium">{r.description}</td>
                    <td className="p-2 text-[10px]">{r.category_name}{r.subcategory ? ` · ${r.subcategory}` : ''}</td>
                    <td className="p-2 text-right">{r.quantity}</td>
                    <td className="p-2">{r.unit}</td>
                    <td className="p-2"><Badge variant="outline" className="text-[9px]">{r.condition}</Badge></td>
                    <td className="p-2 text-right">{r.rate === null || r.rate === undefined ? <span className="text-amber-600 text-[10px]">VALUE N/A</span> : Number(r.rate).toLocaleString()}</td>
                    <td className="p-2 text-[10px] truncate max-w-[120px]">{r.vendor || '—'}</td>
                    <td className="p-2 text-right"><Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => delRow(r.id)} data-testid={`ia-del-${r.id}`}><Trash2 className="w-3 h-3 text-red-600" /></Button></td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={8} className="p-4 text-center text-muted-foreground text-xs">No owner interior assets yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default InteriorAssetModal;
